import React from 'react';
import type { NextPage } from 'next';
import { getBlogArticles } from 'day8-react/build/content';
import WPPageWrapper from '../components/WPPageWrapper';

import { getDestinations, getSocialFeed } from '../helpers/api';
import { DestinationType, WpPageType } from '../globalTypes';
import DestinationPageWrapper from '../components/DestinationPageWrapper';
import wp, { sanitizeDestinationResponse } from '../helpers/wp';
import { sanitizeWpResponse } from '../helpers/wp';

type Page = {
  page: WpPageType
  destinations?: DestinationType[]
  invisibleDestinations?: DestinationType[],
  howItWorksMiniData?: WpPageType
  frontpageExtra?: {}
  includedData?: {}
  destinationBlogs: []
  socialFeed: []
}

const CATEGORY_DESTINATIONS_ALL = 25;
const CATEGORY_DESTINATIONS_INVISIBLE = 43;
const baseUrl = process.env.apiUrl;

const isDestinationPage = (page: WpPageType) => (
  page && page.categories && (page.categories.includes(CATEGORY_DESTINATIONS_ALL) || page.categories.includes(CATEGORY_DESTINATIONS_INVISIBLE))
);
const Page: NextPage<Page> = ({ page, destinations, destinationBlogs, socialFeed }) => {
  if (isDestinationPage(page) && destinations) {
    return <DestinationPageWrapper page={page as DestinationType} destinations={destinations} destinationBlogs={destinationBlogs} socialFeed={socialFeed} />;
  }
  return <WPPageWrapper page={page} destinations={destinations} />;
};
export async function getStaticPaths() {
  const destinations = await getDestinations();
  const paths = destinations.map((destination: { slug: string }) => ({
    params: {
      slug: [destination.slug]
    }
  }));
  return {
    paths,
    fallback: 'blocking'
  };
}

export const getStaticProps = async ({ params }: { params: { slug: string[] } }) => {
  try {
    let parentId;
    let socialFeed = [];
    let destinationBlogs = null;
    const segments = params?.slug;
    let childSlug = segments[0];
    if (segments.length > 1) {
      const parentSlug = segments[0];
      const pages = await wp.pages().slug(parentSlug).get();
      const parent = pages.length > 0 ? pages[0] : null;
      parentId = parent?.id;
      childSlug = segments[1];
    }
    let pages;
    if (parentId) {
      pages = await wp.pages().slug(childSlug).parent(parentId).get();
    } else {
      pages = await wp.pages().slug(childSlug).get();
    }
    let page = pages[0];
    const destinations = await getDestinations();
    if (isDestinationPage(page)) {
      socialFeed = await getSocialFeed();
      if (page?.acf.blog_posts) {
        destinationBlogs = await getBlogArticles(1, page.acf.blog_posts.blog_post_category.slug);
      }
      page = destinations.find((d: DestinationType) => d.slug === childSlug);
      // in case WP destination page doesn't have corresponding invisible destination in TYW (e.g. greece-applebum)
      if (!page) {
        page = pages[0];
      }
    }
    const is404 = page === undefined || !page || !page.title;
    if (is404) {
      return { notFound: true, revalidate: 300 };
    }
    return {
      props: {
        destinations: destinations.map((d: DestinationType) => sanitizeDestinationResponse(d)),
        page: { ...sanitizeDestinationResponse(page), ...sanitizeWpResponse(page) },
        destinationBlogs: destinationBlogs ? destinationBlogs.posts.map((blog: WpPageType) => sanitizeWpResponse(blog)) : null,
        socialFeed
      },
      revalidate: 300
    };
  } catch (e) {
    return { notFound: true };
  }
};
export default Page;